import { useAccount } from '../../../../../client/modules/account/AccountProvider'
import { getAgeAtDate, makeDate } from '../../../../../utils/dateUtils'
import {
  getItemsWithSummaryReview,
  toCamelCase
} from '../../../../../utils/schemaUtils'
import VisitActions from '../VisitActions'
import { type VisitDisplayProps } from '../types'
import { Badge, Box, Flex, Tag, Td, Text, Tr } from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import messages from './messages'

export default function RowItem({
  visit,
  birthDate,
  onVisitSelect,
  onVisitEdit,
  onVisitDelete
}: VisitDisplayProps) {
  const { schema } = useAccount()
  const visitSchema = getItemsWithSummaryReview(schema.visit)
  const { formatMessage } = useIntl()

  const renderValue = (fieldName: string) => {
    const value = visit[toCamelCase(fieldName)]

    if (Array.isArray(value)) {
      return (
        <Flex gap={1} wrap="wrap">
          {value.map((item: string) => (
            <Tag
              key={item}
              size="sm"
              bg="gray.100"
              color="gray.700"
              borderRadius="6px"
            >
              {item}
            </Tag>
          ))}
        </Flex>
      )
    }

    if (value === null || value === undefined || value === '') {
      return <Text color="gray.400">—</Text>
    }

    return (
      <Text color="gray.700" noOfLines={2} maxW="260px">
        {String(value)}
      </Text>
    )
  }

  return (
    <Tr
      data-testid={`visit-${String(visit.id)}`}
      sx={{
        '&:last-of-type td': { borderBottom: 0 },
        '&:hover td': { background: 'var(--chakra-colors-gray-50)' }
      }}
    >
      <Td px={4} py={3.5} minW="210px" borderColor="gray.100">
        <Flex align="center" gap={3}>
          <Badge
            flexShrink={0}
            px={2}
            py={1}
            bg={visit.isFollowUp ? 'gray.900' : 'gray.100'}
            color={visit.isFollowUp ? 'white' : 'gray.700'}
            borderRadius="6px"
            fontSize="10px"
            letterSpacing="0.04em"
          >
            {visit.isFollowUp
              ? formatMessage(messages.followUp)
              : formatMessage(messages.visit)}
          </Badge>
          <Box minW={0}>
            <Text color="gray.900" fontSize="sm" fontWeight="600">
              {makeDate(visit.date)}
            </Text>
            <Text color="gray.500" fontSize="xs" whiteSpace="nowrap">
              {getAgeAtDate(birthDate, visit.date)}
            </Text>
          </Box>
        </Flex>
      </Td>

      {visitSchema.map((fieldName) => (
        <Td
          key={`${visit.id as string}-${fieldName}`}
          px={4}
          py={3.5}
          minW="150px"
          borderColor="gray.100"
          fontSize="sm"
          verticalAlign="top"
        >
          {renderValue(fieldName)}
        </Td>
      ))}

      <Td
        data-sticky
        position="sticky"
        insetInlineEnd={0}
        zIndex={1}
        px={2}
        py={2.5}
        bg="white"
        borderColor="gray.100"
        textAlign="end"
      >
        <VisitActions
          visitId={visit.id}
          onVisitSelect={onVisitSelect}
          onVisitEdit={onVisitEdit}
          onVisitDelete={onVisitDelete}
        />
      </Td>
    </Tr>
  )
}
